import React, { useEffect, useMemo, useState } from "react";
import { previewJob } from "../../../api/migration";
import { Backdrop } from "../../../components/ui/Modal";
import type { MigrationPreviewResponse } from "../../../type";
import { extractApiError } from "../helpers";
import type { CartRow } from "./MigrationCartPanel";

interface Props {
  open: boolean;
  cartList: CartRow[];
  inProductPks: Set<number>;
  scheduledAt: string | null;
  isSubmitting: boolean;
  onProceed: () => void;
  onCancel: () => void;
}

const DISPLAY_LIMIT = 100;

/**
 * Final-review modal shown when the admin clicks "Create + run" / "Schedule job".
 *
 * Surfaces three things an auditor needs before any data is actually
 * published to product:
 *   1. The concrete cart contents (up to DISPLAY_LIMIT rows shown, total count
 *      above).
 *   2. Server-side preview counts (match / skip / cascade) — called fresh on
 *      open so the numbers match the moment of confirmation, not whenever
 *      the last inline Preview ran.
 *   3. Schedule timing (if any).
 *
 * The parent (MigrationTab) owns `open` and the submitting state; this
 * component is presentational + fetches preview on mount.
 */
const MigrationReviewModal: React.FC<Props> = ({
  open,
  cartList,
  inProductPks,
  scheduledAt,
  isSubmitting,
  onProceed,
  onCancel,
}) => {
  const [preview, setPreview] = useState<MigrationPreviewResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cartSignature = useMemo(
    () => cartList.map((r) => r.index).sort((a, b) => a - b).join(","),
    [cartList]
  );

  // Refresh preview every time the modal opens (or the cart identity changes
  // while open — rare, but possible if the modal is kept mounted).
  useEffect(() => {
    if (!open) {
      setPreview(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    (async () => {
      try {
        const pks = cartList.map((r) => r.index);
        const resp = await previewJob({
          filter: { index: pks },
          search: "",
        });
        if (!cancelled) setPreview(resp);
      } catch (err) {
        if (!cancelled) setError(extractApiError(err, "Preview failed."));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // cartSignature captures identity; excluding cartList itself keeps this
    // stable if the same set is passed back in a new array reference.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cartSignature]);

  if (!open) return null;

  const visibleRows = cartList.slice(0, DISPLAY_LIMIT);
  const hiddenCount = Math.max(0, cartList.length - DISPLAY_LIMIT);
  const netInsertable = preview
    ? preview.total - preview.already_in_product_count
    : null;
  const canProceed =
    !isLoading && !isSubmitting && netInsertable !== null && netInsertable > 0;

  return (
    <Backdrop onClose={isSubmitting ? undefined : onCancel} size="2xl">
      <div className="flex flex-col max-h-[85vh] -m-6">
        <div className="flex items-start justify-between p-5 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {scheduledAt ? "Schedule migration job" : "Review before run"}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Confirm the rows below will be copied to the product database.
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="text-sm text-gray-500 hover:text-gray-900 disabled:opacity-40"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="flex-1 p-5 space-y-4 overflow-y-auto">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Stat label="Cart" value={cartList.length.toLocaleString()} />
            <Stat
              label="Matched"
              value={preview ? preview.total.toLocaleString() : "..."}
            />
            <Stat
              label="Will process"
              tone="blue"
              value={
                preview
                  ? preview.total.toLocaleString()
                  : "..."
              }
            />
            <Stat
              label="Cascade meta"
              value={
                preview
                  ? preview.cascading_metadata_count.toLocaleString()
                  : "..."
              }
            />
          </div>

          {scheduledAt && (
            <div className="p-3 text-sm border rounded bg-indigo-50 border-indigo-200 text-indigo-900">
              Scheduled to run at{" "}
              <span className="font-medium">
                {new Date(scheduledAt).toLocaleString("en-US")}
              </span>
              .
            </div>
          )}

          {preview && preview.already_in_product_count > 0 && (
            <div className="p-3 text-xs border rounded bg-gray-50 border-gray-200 text-gray-700">
              {preview.already_in_product_count.toLocaleString()} row(s)
              already on product will be updated with the latest changes.
            </div>
          )}

          {error && (
            <div
              className="p-3 text-sm text-red-800 border border-red-200 rounded bg-red-50"
              role="alert"
            >
              {error}
            </div>
          )}

          <div className="border border-gray-200 rounded">
            <div className="px-3 py-2 text-xs text-gray-500 uppercase bg-gray-50 border-b border-gray-200">
              Rows to copy ({cartList.length.toLocaleString()} total
              {hiddenCount > 0 ? `, showing first ${DISPLAY_LIMIT}` : ""})
            </div>
            <div className="overflow-y-auto max-h-[28rem]">
              <table className="w-full text-xs text-left">
                <thead className="sticky top-0 text-[10px] text-gray-500 uppercase bg-white border-b border-gray-100">
                  <tr>
                    <th className="px-3 py-2">index</th>
                    <th className="px-3 py-2">experiment_id</th>
                    <th className="px-3 py-2">il_name</th>
                    <th className="px-3 py-2">cargo</th>
                    <th className="px-3 py-2">status</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((row) => {
                    const inProd = inProductPks.has(row.index);
                    return (
                      <tr
                        key={row.index}
                        className={`border-t border-gray-100 ${
                          inProd ? "text-gray-400" : "text-gray-800"
                        }`}
                      >
                        <td className="px-3 py-1.5 tabular-nums">{row.index}</td>
                        <td className="px-3 py-1.5">
                          {row.experiment_id ?? "—"}
                        </td>
                        <td className="px-3 py-1.5">{row.il_name ?? "—"}</td>
                        <td className="px-3 py-1.5">{row.cargo ?? "—"}</td>
                        <td className="px-3 py-1.5">
                          {inProd ? (
                            <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-gray-200 text-gray-600">
                              update (in prod)
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-blue-50 text-blue-700">
                              insert
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {hiddenCount > 0 && (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-3 py-2 text-xs text-center text-gray-400"
                      >
                        + {hiddenCount.toLocaleString()} more row(s) not shown
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onProceed}
            disabled={!canProceed}
            className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {isSubmitting
              ? "Working..."
              : scheduledAt
              ? "Schedule job"
              : "Run now"}
          </button>
        </div>
      </div>
    </Backdrop>
  );
};

const Stat: React.FC<{
  label: string;
  value: React.ReactNode;
  tone?: "blue";
}> = ({ label, value, tone }) => (
  <div>
    <dt className="text-[10px] uppercase text-gray-500">{label}</dt>
    <dd
      className={`text-lg font-semibold ${
        tone === "blue" ? "text-blue-700" : "text-gray-900"
      }`}
    >
      {value}
    </dd>
  </div>
);

export default MigrationReviewModal;
